import Analytics from '@web-solutions/module-analytics';

import { SUPPORTED_LANGUAGES } from '../localization/constants';

const LOCALE_KEYS: string[] = Object.keys(SUPPORTED_LANGUAGES);
const FALLBACK_LOCALE = 'en';
const MAX_REPORTED_PATHS = 30;
const SERVICE_KEYS = ['_persist'];

export interface EmptyConfigValues {
  /** пути, которые юзер увидит пустыми: тексты, ключи картинок, ссылки */
  missing: string[];
  /** незаполненные переводы — юзер увидит английский текст вместо своего языка */
  translations: string[];
}

/** feedbacks.0.title -> feedbacks.title, чтобы allowedEmpty не зависел от индексов */
const stripIndices = (path: string) => path.replace(/\.\d+(?=\.|$)/g, '');

/**
 * Ключ считается локалью только если рядом лежит en: коды локалей совпадают
 * с обычными именами полей (`id` — это и «Indonesian», и идентификатор продукта).
 */
const isTranslationLeaf = (key: string, parent: unknown) =>
  key !== FALLBACK_LOCALE &&
  LOCALE_KEYS.includes(key) &&
  !!parent &&
  typeof parent === 'object' &&
  FALLBACK_LOCALE in parent;

/**
 * В allowedEmpty можно указать конкретный путь (`upsaleDynamic.product`),
 * целую ветку (`upsaleDynamic`) или префикс ключа (`mng*`).
 */
const isAllowedEmpty = (path: string, allowed: Set<string>, prefixes: string[]) => {
  const normalized = stripIndices(path);

  if (prefixes.some((prefix) => normalized.startsWith(prefix))) {
    return true;
  }

  const parts = normalized.split('.');

  for (let i = parts.length; i > 0; i--) {
    if (allowed.has(parts.slice(0, i).join('.'))) {
      return true;
    }
  }

  return false;
};

export const findEmptyConfigValues = (
  config: object,
  allowedEmpty: readonly string[] = [],
): EmptyConfigValues => {
  const prefixes = allowedEmpty.filter((p) => p.endsWith('*')).map((p) => p.slice(0, -1));
  const allowed = new Set(allowedEmpty.filter((p) => !p.endsWith('*')));
  const missing: string[] = [];
  const translations: string[] = [];

  const walk = (node: unknown, path: string, key: string, parent: unknown) => {
    if (typeof node === 'string') {
      if (node.trim() || isAllowedEmpty(path, allowed, prefixes)) {
        return;
      }
      (isTranslationLeaf(key, parent) ? translations : missing).push(path);
      return;
    }

    if (Array.isArray(node)) {
      node.forEach((item, index) => walk(item, `${path}.${index}`, String(index), node));
      return;
    }

    if (node && typeof node === 'object') {
      Object.entries(node).forEach(([childKey, value]) => {
        if (SERVICE_KEYS.includes(childKey)) {
          return;
        }
        walk(value, path ? `${path}.${childKey}` : childKey, childKey, node);
      });
    }
  };

  walk(config, '', '', undefined);

  return { missing, translations };
};

/**
 * Печать в консоль выключена по умолчанию — список пустых значений длинный
 * и мешает работать. Включается на время разбора конфига:
 *   ?rcDebug=1 в урле или localStorage.setItem('rc_debug', '1')
 */
const isConsoleReportEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return (
      new URLSearchParams(window.location.search).get('rcDebug') === '1' ||
      window.localStorage.getItem('rc_debug') === '1'
    );
  } catch (err) {
    return false;
  }
};

/**
 * Дефолты из INITIAL_CONFIG прячут ошибку конфига от юзера — он увидит
 * английский текст вместо пустоты. Поэтому о незаполненных значениях
 * узнаём отсюда: событием в аналитике, а по флагу — ещё и в консоли.
 */
export const createRemoteConfigValidator = (allowedEmpty: readonly string[] = []) => {
  // init и reInit прогоняют валидацию по очереди — репортим каждый путь один раз
  const reported = new Set<string>();

  const takeNew = (paths: string[]) => {
    const fresh = paths.filter((path) => !reported.has(path));
    fresh.forEach((path) => reported.add(path));
    return fresh;
  };

  return (cfg: object): EmptyConfigValues => {
    const { missing, translations } = findEmptyConfigValues(cfg, allowedEmpty);

    const freshMissing = takeNew(missing);
    const freshTranslations = takeNew(translations);

    if (isConsoleReportEnabled()) {
      if (freshMissing.length) {
        console.warn('[REMOTE CONFIG] пусто в мутаторе и в INITIAL_CONFIG:', freshMissing);
      }
      if (freshTranslations.length) {
        console.warn('[REMOTE CONFIG] нет перевода, покажем en:', freshTranslations);
      }
    }

    if (freshMissing.length) {
      Analytics.trackEvent('remote_config', 'empty_values', {
        count: freshMissing.length,
        paths: freshMissing.slice(0, MAX_REPORTED_PATHS).join(','),
        truncated: freshMissing.length > MAX_REPORTED_PATHS,
      });
    }

    if (freshTranslations.length) {
      Analytics.trackEvent('remote_config', 'empty_translations', {
        count: freshTranslations.length,
        paths: freshTranslations.slice(0, MAX_REPORTED_PATHS).join(','),
        truncated: freshTranslations.length > MAX_REPORTED_PATHS,
      });
    }

    return { missing, translations };
  };
};
